import React, { useState, useEffect } from "react";
import { getAuth } from "../Config/matuAuth";
import { getDatabase, ref, get } from "../Config/matuRtdb";

export default function DatabaseDebugger() {
  const [userInfo, setUserInfo] = useState(null);
  const [databaseInfo, setDatabaseInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDebugInfo = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        const db = getDatabase();

        if (!user) {
          setError("No hay usuario autenticado");
          setIsLoading(false);
          return;
        }

        console.log("🔍 Usuario autenticado:", user);

        // Buscar información del usuario en diferentes rutas
        const paths = [
          `usuarios/${user.uid}`,
          `BetPay/${user.uid}`,
          `users/${user.uid}`
        ];

        const results = {};

        for (const path of paths) {
          try {
            const refPath = ref(db, path);
            const snapshot = await get(refPath);
            
            if (snapshot.exists()) {
              results[path] = snapshot.val();
              console.log(`✅ Encontrado en ${path}:`, snapshot.val());
            } else {
              results[path] = null;
              console.log(`❌ No encontrado en ${path}`);
            }
          } catch (err) {
            results[path] = { error: err.message };
            console.log(`❌ Error en ${path}:`, err);
          }
        }

        setUserInfo({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        });

        setDatabaseInfo(results);
        setIsLoading(false);

      } catch (err) {
        console.error("Error cargando debug info:", err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    loadDebugInfo();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando información de depuración...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            🔧 Debug de Base de Datos - Super Admin
          </h1>
          <p className="text-gray-600 mb-6">
            Esta página te ayuda a diagnosticar problemas con el acceso al Super Admin Dashboard.
          </p>
        </div>

        {/* Información del usuario */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">👤 Información del Usuario</h2>
          {userInfo && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-600">UID</p>
                <p className="text-lg font-mono bg-gray-100 p-2 rounded">{userInfo.uid}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Email</p>
                <p className="text-lg">{userInfo.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Nombre</p>
                <p className="text-lg">{userInfo.displayName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600">Foto</p>
                <p className="text-lg">{userInfo.photoURL || 'N/A'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Información de la base de datos */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">🗄️ Datos en Base de Datos</h2>
          
          {databaseInfo && Object.entries(databaseInfo).map(([path, data]) => (
            <div key={path} className="mb-6 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                📁 {path}
              </h3>
              
              {data === null ? (
                <div className="text-red-600 bg-red-50 p-3 rounded">
                  ❌ No se encontraron datos en esta ruta
                </div>
              ) : data.error ? (
                <div className="text-red-600 bg-red-50 p-3 rounded">
                  ❌ Error: {data.error}
                </div>
              ) : (
                <div>
                  <div className="mb-3">
                    <span className="text-green-600 bg-green-50 px-2 py-1 rounded text-sm">
                      ✅ Datos encontrados
                    </span>
                  </div>
                  
                  {/* Campos importantes */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">isSuperAdmin</p>
                      <p className={`text-lg font-bold ${
                        data.isSuperAdmin ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {String(data.isSuperAdmin)} ({typeof data.isSuperAdmin})
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">tipoCuenta</p>
                      <p className="text-lg">{data.tipoCuenta || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">isVip</p>
                      <p className={`text-lg ${data.isVip ? 'text-green-600' : 'text-gray-600'}`}>
                        {String(data.isVip || false)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">name</p>
                      <p className="text-lg">{data.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">email</p>
                      <p className="text-lg">{data.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">createdAt</p>
                      <p className="text-lg">{data.createdAt ? new Date(data.createdAt).toLocaleString() : 'N/A'}</p>
                    </div>
                  </div>

                  {/* Datos completos */}
                  <details className="mt-4">
                    <summary className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium">
                      📋 Ver todos los datos
                    </summary>
                    <pre className="mt-2 bg-gray-100 p-4 rounded text-sm overflow-x-auto">
                      {JSON.stringify(data, null, 2)}
                    </pre>
                  </details>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Diagnóstico */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">🔍 Diagnóstico</h2>
          
          {databaseInfo && (
            <div className="space-y-4">
              {/* Verificar si se encontró el usuario */}
              {Object.values(databaseInfo).some(data => data && !data.error) ? (
                <div className="text-green-600 bg-green-50 p-3 rounded">
                  ✅ Usuario encontrado en la base de datos
                </div>
              ) : (
                <div className="text-red-600 bg-red-50 p-3 rounded">
                  ❌ Usuario NO encontrado en ninguna ruta de la base de datos
                </div>
              )}

              {/* Verificar isSuperAdmin */}
              {Object.entries(databaseInfo).map(([path, data]) => {
                if (data && !data.error && data.isSuperAdmin !== undefined) {
                  const hasSuperAdmin = data.isSuperAdmin === true || 
                                       data.isSuperAdmin === "true" || 
                                       data.isSuperAdmin === 1 ||
                                       data.isSuperAdmin === "1" ||
                                       data.isSuperAdmin === "yes" ||
                                       data.isSuperAdmin === "YES";
                  
                  return (
                    <div key={path} className={`p-3 rounded ${
                      hasSuperAdmin ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                    }`}>
                      {hasSuperAdmin ? '✅' : '❌'} isSuperAdmin en {path}: {String(data.isSuperAdmin)} ({typeof data.isSuperAdmin})
                    </div>
                  );
                }
                return null;
              })}

              {/* Recomendaciones */}
              <div className="bg-blue-50 p-4 rounded">
                <h3 className="font-bold text-blue-800 mb-2">💡 Recomendaciones:</h3>
                <ul className="text-blue-700 space-y-1 text-sm">
                  <li>• Si no ves datos, verifica que tu UID esté correcto</li>
                  <li>• Si isSuperAdmin es false, necesitas cambiarlo a true en Firebase</li>
                  <li>• Si el tipo es string, asegúrate de que sea "true" o "yes"</li>
                  <li>• Verifica que estés logueado con la cuenta correcta</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Acciones */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">⚡ Acciones</h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              🔄 Actualizar
            </button>
            <button
              onClick={() => window.open('/super-admin-dashboard', '_blank')}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              🚀 Probar Super Admin Dashboard
            </button>
            <button
              onClick={() => window.history.back()}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              ← Volver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 